import { getIsMobile } from "./getIsMobile";
import { isMacLike, keyMatcher } from "./keyMatcher";

import type { KeyCombination, KeyCode } from "@corca/data";

type ClickWithAlt = "Click" | "Alt+Click";
type ClickWithShift = ClickWithAlt | `Shift+${ClickWithAlt}`;
type ClickWithMod = ClickWithShift | `Mod+${ClickWithShift}`;
type ClickWithCtrl = ClickWithMod | `Ctrl+${ClickWithMod}`;

/**
 * A pointer gesture qualified by modifiers, ordered like {@link KeyCombination}.
 * Display-only: dispatched from mouse handlers, never matched by the key matcher.
 */
export type ClickCombination = ClickWithCtrl | `Meta+${ClickWithCtrl}`;

/** A touch gesture standing in for a modifier click, which a touch screen has no way to express. */
export type TouchGesture = "LongPress";

/**
 * For gestures whose Mac form differs by more than a glyph from the Windows/Linux one.
 * `touch` names the mobile stand-in, and is shown in place of both on a phone.
 */
export type PlatformCombination = {
  mac: ClickCombination;
  other: ClickCombination;
  touch?: TouchGesture;
};

// For later use: allow user to remap hotkeys
const KEY_HOTKEY_MAP = {
  "suggestion-1": "Mod+Digit1",
  "suggestion-2": "Mod+Digit2",
  "suggestion-3": "Mod+Digit3",
  "suggestion-4": "Mod+Digit4",
  "suggestion-5": "Mod+Digit5",
  "suggestion-6": "Mod+Digit6",
  "suggestion-7": "Mod+Digit7",
  "suggestion-8": "Mod+Digit8",
  "suggestion-9": "Mod+Digit9",
  "active-suggestion": "Enter",
  "add-row": "Shift+Enter",
  "send-message": "Enter",
  "add-column": "Shift+Space",
  "open-chat-with-bot": "Mod+Alt+Backslash",
  "open-chat-with-team": "Mod+Alt+Slash",
  search: "Mod+Slash",
  copy: "Mod+KeyC",
  cut: "Mod+KeyX",
  paste: "Mod+KeyV",
  "duplicate-line": "Mod+KeyD",
  "save-image": "Mod+KeyS",
  "toggle-sidebars": "Mod+Backslash",
  "align-left": "Mod+Shift+KeyL",
  "align-center": "Mod+Shift+KeyE",
  "align-right": "Mod+Shift+KeyR",
  bold: "Mod+KeyB",
  italic: "Mod+KeyI",
  underline: "Mod+KeyU",
  strikethrough: "Mod+Shift+KeyS",
  inlineCode: "Mod+KeyE",
  "collapse-block": "Mod+Shift+Period",
  "block-controls": "Mod+Alt+Period",
} satisfies Record<string, KeyCombination>;

export const CLICK_HOTKEY_MAP = {
  "go-to-source": "Mod+Click",
  // Alt+Click alone is reserved by Linux window managers, hence the split.
  "duplicate-at-cursor": { mac: "Alt+Click", other: "Mod+Shift+Click", touch: "LongPress" },
  "reference-at-cursor": "Click",
} satisfies Record<string, ClickCombination | PlatformCombination>;

export const HOTKEY_MAP = { ...KEY_HOTKEY_MAP, ...CLICK_HOTKEY_MAP };

const DISPLAY_KEY_MAP: Partial<Record<KeyCode, string>> = {
  Enter: "Enter",
  Space: "Space",
  Esc: "Esc",
  Backspace: "Backspace",
  Delete: "Delete",
  Escape: "Escape",
  ArrowLeft: "←",
  ArrowRight: "→",
  ArrowUp: "↑",
  ArrowDown: "↓",
  Home: "Home",
  End: "End",
  Tab: "Tab",
  Comma: ",",
  Period: ".",
  Slash: "/",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Backquote: "`",
  BracketLeft: "[",
  BracketRight: "]",
};

/**
 * Convert a hotkey from our internal representation to the format expected by Prosemirror's keymap plugin.
 * @example
 * toProsemirrorHotkey("duplicate-line") // returns "Mod-d"
 */
export function toProsemirrorHotkey(hotkey: KeyHotkey): string {
  return KEY_HOTKEY_MAP[hotkey]
    .split("+")
    .map((key) => {
      if (key.startsWith("Digit")) {
        return key.replace("Digit", "");
      }
      if (key.startsWith("Key")) {
        return key.replace("Key", "").toLowerCase();
      }

      return DISPLAY_KEY_MAP[key as KeyCode] ?? key;
    })
    .join("-");
}

export function isKnownHotkey(hotkey: unknown): hotkey is Hotkey {
  return typeof hotkey === "string" && hotkey in HOTKEY_MAP;
}

export function matchHotkey(
  hotkey: KeyHotkey,
  event: KeyboardEvent | React.KeyboardEvent,
): boolean {
  return keyMatcher(KEY_HOTKEY_MAP[hotkey], event);
}

export function isOpenSuggestionsShortcut(event: KeyboardEvent | React.KeyboardEvent): boolean {
  return (
    keyMatcher("Alt+Space", event) ||
    keyMatcher("Mod+Space", event) ||
    keyMatcher("Ctrl+Space", event)
  );
}

export type Hotkey = keyof typeof HOTKEY_MAP;

/** Hotkeys bound to a key press, i.e. everything the key matcher can dispatch. */
export type KeyHotkey = keyof typeof KEY_HOTKEY_MAP;

export type ClickHotkey = keyof typeof CLICK_HOTKEY_MAP;

/** A hotkey in `aria-keyshortcuts` syntax, such as `Control+Digit1` as `Control+1`. */
export function ariaKeyShortcuts(hotkey: Hotkey): string {
  const combination = HOTKEY_MAP[hotkey];
  const str =
    typeof combination === "string" ? combination : combination[isMacLike ? "mac" : "other"];

  return str
    .split("+")
    .map((key) => {
      switch (key) {
        case "Mod":
          return isMacLike ? "Meta" : "Control";
        case "Ctrl":
          return "Control";
        default:
          return key.replace(/^(Key|Digit)/, "");
      }
    })
    .join("+");
}

export type SpokenKeyName =
  | "control"
  | "command"
  | "option"
  | "alt"
  | "leftArrow"
  | "rightArrow"
  | "upArrow"
  | "downArrow";

/** A key's name as a screen reader should speak it, where its code or glyph reads poorly. */
function spokenKey(key: string, spoken: (name: SpokenKeyName) => string): string {
  switch (key) {
    case "Mod":
      return spoken(isMacLike ? "command" : "control");
    case "Ctrl":
      return spoken("control");
    case "Meta":
      return spoken("command");
    case "Alt":
      return spoken(isMacLike ? "option" : "alt");
    case "ArrowLeft":
      return spoken("leftArrow");
    case "ArrowRight":
      return spoken("rightArrow");
    case "ArrowUp":
      return spoken("upArrow");
    case "ArrowDown":
      return spoken("downArrow");
    default:
      return key;
  }
}

/**
 * Convert a hotkey from our internal representation to a human-readable format for display in the UI.
 * With `spoken`, it is worded for a screen reader instead: key names, no glyphs or `+`.
 * @example
 * displayHotkey("duplicate-line") // returns "Ctrl+Shift+D" on Windows/Linux and "⌘⇧D" on Mac
 * displayHotkey("go-to-source") // returns "Ctrl+Click" on Windows/Linux and "⌘Click" on Mac
 * displayHotkey("duplicate-at-cursor") // returns "Long Press" on a phone
 * displayHotkey("block-controls", { spoken: (name) => t(`spokenKeys.${name}`) }) // "Control Alt Period", or "Command Option Period" on Mac
 */
export function displayHotkey(
  hotkey: Hotkey,
  { spoken }: { spoken?: (name: SpokenKeyName) => string } = {},
) {
  const combination = HOTKEY_MAP[hotkey];

  if (typeof combination !== "string" && combination.touch && getIsMobile()) {
    // A gesture name carries no modifiers, so it skips the rewriting below.
    switch (combination.touch) {
      case "LongPress":
        return "Long Press";
    }
  }

  let str: string =
    typeof combination === "string" ? combination : combination[isMacLike ? "mac" : "other"];

  str = str
    .split("+")
    .map((key) => {
      // Spoken names leave no `Mod` or `+` for the glyph rewrites below to change.
      if (spoken) {
        return spokenKey(key, spoken);
      }
      if (key in DISPLAY_KEY_MAP) {
        return DISPLAY_KEY_MAP[key as KeyCode];
      }
      return key;
    })
    .join(spoken ? " " : "+");

  if (str.indexOf("Mod") !== -1) {
    if (isMacLike) {
      str = str.replace("Mod+", "⌘");
    } else {
      str = str.replace("Mod", "Ctrl");
    }
  }

  if (str.indexOf("Shift") !== -1) {
    if (isMacLike) {
      str = str.replace("Shift+", "⇧");
    }
  }

  if (str.indexOf("Alt") !== -1) {
    if (isMacLike) {
      str = str.replace("Alt+", "⌥");
    }
  }

  str = str.replace("Digit", "").replace("Key", "");

  return str;
}
